import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store";
import type { InactiveNotificationTemplate } from "@/types/notifications";
import {
  fetchNotificationsInactive,
  createNotificationsInactive,
  updateNotificationsInactive,
  deleteNotificationsInactive,
} from "@/store/modules/notificationsInactive/thunks";

const useNotificationsInactive = (onSuccess?: () => void) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector(
    (state: any) =>
      state.notificationsInactive || {
        data: null as InactiveNotificationTemplate[] | null,
        loading: false,
        error: null,
      },
  );

  const refresh = useCallback(
    (params?: any) => {
      dispatch(fetchNotificationsInactive(params || ({} as any)));
    },
    [dispatch],
  );

  const createTemplate = useCallback(
    async (payload: Omit<InactiveNotificationTemplate, "id">) => {
      const result = await dispatch(createNotificationsInactive(payload));
      if (createNotificationsInactive.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const updateTemplate = useCallback(
    async (payload: Partial<InactiveNotificationTemplate> & { id: number }) => {
      const result = await dispatch(updateNotificationsInactive(payload));
      if (updateNotificationsInactive.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  const removeTemplate = useCallback(
    async (id: number) => {
      const result = await dispatch(deleteNotificationsInactive(id));
      if (deleteNotificationsInactive.fulfilled.match(result) && onSuccess)
        onSuccess();
      return result;
    },
    [dispatch, onSuccess],
  );

  return {
    templates: data,
    loading,
    error,
    refresh,
    createTemplate,
    updateTemplate,
    removeTemplate,
  };
};

export default useNotificationsInactive;
